import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { addCompany, addCompanyUserInvitation, addTrigger, writeCompanyAudit, Timestamp } from "@/lib/firebase";
import { Loader2, FlaskConical, Mail, Plus, X } from "lucide-react";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/auth-context";

interface AddTrialModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const validateEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export default function AddTrialModal({ isOpen, onClose }: AddTrialModalProps) {
  const [companyName, setCompanyName] = useState("");
  const [companyType, setCompanyType] = useState<string>("Customer");
  const [trialExpiryDate, setTrialExpiryDate] = useState("");
  const [emails, setEmails] = useState<string[]>([""]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const { user } = useAuth();

  const addEmailField = () => setEmails([...emails, ""]);

  const removeEmailField = (index: number) => {
    if (emails.length === 1) return;
    setEmails(emails.filter((_, i) => i !== index));
  };

  const updateEmail = (index: number, value: string) => {
    const updated = [...emails];
    updated[index] = value;
    setEmails(updated);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!companyName.trim()) newErrors.companyName = "Company name is required";
    if (!companyType) newErrors.companyType = "Type is required";
    if (!trialExpiryDate) {
      newErrors.trialExpiryDate = "Trial expiry date is required";
    } else if (new Date(trialExpiryDate) <= new Date()) {
      newErrors.trialExpiryDate = "Trial expiry date must be in the future";
    }

    const filledEmails = emails.filter(e => e.trim());
    if (filledEmails.length === 0) {
      newErrors.emails = "At least one user email is required";
    } else {
      const invalidEmail = filledEmails.find(e => !validateEmail(e.trim()));
      if (invalidEmail) newErrors.emails = `Invalid email address: ${invalidEmail}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const expiryTimestamp = Timestamp.fromDate(new Date(trialExpiryDate));

      const companyId = await addCompany({
        name: companyName.trim(),
        type: companyType,
        enabled: true,
        logoUrl: null,
        status: "newCompany",
        trialExpiryDate: expiryTimestamp,
        created: Timestamp.now(),
      });
      logger.log("✅ Trial company created:", companyId);

      const filledEmails = emails.filter(e => e.trim());

      await writeCompanyAudit(
        companyId,
        companyName.trim(),
        'trial_created',
        user?.email ?? 'unknown',
        user?.uid ?? '',
        [
          { field: 'name', from: '', to: companyName.trim() },
          { field: 'type', from: '', to: companyType },
          { field: 'trialExpiryDate', from: '', to: trialExpiryDate },
          { field: 'users', from: '', to: filledEmails.join(', ') },
        ]
      );

      for (const email of filledEmails) {
        await addCompanyUserInvitation({
          companyId,
          email: email.trim(),
          role: "user",
          status: "Pending",
          inviteDate: null,
          error: "",
          sent: false,
          created: Timestamp.now(),
        });
      }
      logger.log("✅ Trial user invitations created:", filledEmails.length);

      await addTrigger({
        type: "inviteUsers",
        status: "Open",
        referenceId: companyId,
        created: Timestamp.now(),
      });
      logger.log("✅ Trigger created for inviteUsers:", companyId);

      toast({
        title: "Trial created successfully",
        description: `${companyName.trim()} has been added as a trial company with ${filledEmails.length} user invitation(s).`,
      });

      resetForm();
      onClose();
    } catch (error: any) {
      logger.error("❌ Error creating trial:", error);
      toast({
        title: "Failed to create trial",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCompanyName("");
    setCompanyType("Customer");
    setTrialExpiryDate("");
    setEmails([""]);
    setErrors({});
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            Add Trial Company
          </DialogTitle>
          <DialogDescription>
            Create a new trial company and add initial trial users.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="trialCompanyName">
              Company Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="trialCompanyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Enter company name"
              disabled={isSubmitting}
              className={errors.companyName ? "border-red-500" : ""}
            />
            {errors.companyName && <p className="text-sm text-red-600">{errors.companyName}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="trialCompanyType">
              Type <span className="text-red-500">*</span>
            </Label>
            <Select value={companyType} onValueChange={setCompanyType} disabled={isSubmitting}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Customer">Customer</SelectItem>
                <SelectItem value="Partner">Partner</SelectItem>
              </SelectContent>
            </Select>
            {errors.companyType && <p className="text-sm text-red-600">{errors.companyType}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="trialExpiryDate">
              Trial Expiry Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="trialExpiryDate"
              type="date"
              value={trialExpiryDate}
              onChange={(e) => setTrialExpiryDate(e.target.value)}
              disabled={isSubmitting}
              className={errors.trialExpiryDate ? "border-red-500" : ""}
            />
            {errors.trialExpiryDate && <p className="text-sm text-red-600">{errors.trialExpiryDate}</p>}
          </div>

          <div className="space-y-2">
            <Label>
              Trial User Emails <span className="text-red-500">*</span>
            </Label>
            <div className="space-y-2">
              {emails.map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Mail className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => updateEmail(index, e.target.value)}
                      placeholder="user@company.com"
                      disabled={isSubmitting}
                      className={`pl-10 ${errors.emails ? "border-red-500" : ""}`}
                    />
                  </div>
                  {emails.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-9 w-9 p-0 text-slate-400 hover:text-red-600"
                      onClick={() => removeEmailField(index)}
                      disabled={isSubmitting}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {errors.emails && <p className="text-sm text-red-600">{errors.emails}</p>}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addEmailField}
              disabled={isSubmitting}
              className="gap-1"
            >
              <Plus className="h-3 w-3" />
              Add another email
            </Button>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Trial"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
